// AddSpaceServiceModal.tsx — Co Marca — Agregar Espacio de Servicio
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Store, Package } from 'lucide-react';
import { createEspacioServicio } from '../../services/espacioServiciosService';
import { fetchEspacios, fetchServicios, CatalogoItem } from '../../services/catalogosService';

interface AddSpaceServiceModalProps {
    isOpen: boolean; onClose: () => void; onSuccess: () => void;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.assm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes assmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes assmSlideUp{from{opacity:0;transform:translateY(24px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.assm-overlay{animation:assmFadeIn .22s ease both;}
.assm-dialog{animation:assmSlideUp .32s cubic-bezier(.16,1,.3,1) both;}
.assm-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fafafa;font-size:.88rem;color:#374151;outline:none;transition:all .2s;}
.assm-input:focus{border-color:${colors.primary};box-shadow:0 0 0 3px ${colors.primary}12;background:#fff;}
.assm-select{appearance:none;}
.assm-btn-primary{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(193, 52, 139,.28);transition:all .22s cubic-bezier(.16,1,.3,1);}
.assm-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(193, 52, 139,.34);}
.assm-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.assm-btn-ghost{display:inline-flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;color:#374151;border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;}
.assm-btn-ghost:hover{background:#f1f5f9;}
.custom-scroll::-webkit-scrollbar{width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.3);border-radius:20px;}
`;

const AddSpaceServiceModal: React.FC<AddSpaceServiceModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({ espacio:'', servicio:'', estado:true });
    const [espaciosList, setEspaciosList] = useState<CatalogoItem[]>([]);
    const [serviciosList, setServiciosList] = useState<CatalogoItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError]         = useState<string|null>(null);

    useEffect(() => {
        if (isOpen) {
            setFormData({ espacio:'', servicio:'', estado:true }); setError(null);
            fetchEspacios().then(d=>setEspaciosList(d.filter((i:any)=>i.estado!==false))).catch(()=>setEspaciosList([]));
            fetchServicios().then(d=>setServiciosList(d.filter((i:any)=>i.estado!==false))).catch(()=>setServiciosList([]));
        }
    }, [isOpen]);

    const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => setFormData(p=>({...p,[e.target.name]:e.target.value}));

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault(); setError(null); setIsLoading(true);
        try {
            await createEspacioServicio({ espacio:Number(formData.espacio), servicio:Number(formData.servicio), estado:formData.estado });
            onSuccess(); onClose();
        } catch { setError('Error al crear el espacio de servicio. Intente nuevamente.'); }
        finally { setIsLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="assm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="assm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>
                <div className="assm-dialog custom-scroll" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'500px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid #f1f5f9' }}>

                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.secondary},${colors.accentDark},${colors.secondaryLight})`, borderRadius:'24px 24px 0 0' }}/>

                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.primary},${colors.primaryDark})`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${colors.primary}30` }}>
                                    <Plus size={18} color="#fff"/>
                                </div>
                                <div>
                                    <h3 style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Nueva Asociación</h3>
                                    <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Vincula un espacio con un servicio</p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={15} color="#64748b"/>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}>
                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}22`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:colors.primaryDark, fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Package size={11} color={colors.primary}/> Servicio *
                                </label>
                                <select required name="servicio" value={formData.servicio} onChange={handleSelect} className="assm-input assm-select">
                                    <option value="">Selecciona...</option>
                                    {serviciosList.map(i=><option key={i.id} value={i.id}>{i.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Store size={11} color={colors.secondary}/> Espacio *
                                </label>
                                <select required name="espacio" value={formData.espacio} onChange={handleSelect} className="assm-input assm-select">
                                    <option value="">Selecciona...</option>
                                    {espaciosList.map(i=><option key={i.id} value={i.id}>{i.nombre}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={{ background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                            <div>
                                <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a', marginBottom:'2px' }}>Estado</p>
                                <p style={{ fontSize:'.7rem', color:formData.estado?colors.secondaryLight:'#94a3b8', fontWeight:'600' }}>
                                    {formData.estado?'● Activo':'○ Inactivo'}
                                </p>
                            </div>
                            <div onClick={()=>setFormData(p=>({...p,estado:!p.estado}))} style={{ width:'44px', height:'24px', borderRadius:'100px', background:formData.estado?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                <div style={{ position:'absolute', top:'3px', left:formData.estado?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                            </div>
                        </div>

                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} disabled={isLoading} className="assm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={isLoading} className="assm-btn-primary">
                                {isLoading ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</> : <><Plus size={14}/> Crear</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default AddSpaceServiceModal;